/* eslint-disable @typescript-eslint/no-explicit-any */
import { I18nLang, ILang } from '@patsnap/synapse_common_interface'
import { getLangDegraded, getSingle } from '@patsnap/synapse_common_utils'
import { sharedCtx } from '@pharmsnap/shared/context'
import { useLocale } from '@pharmsnap/shared/src/composition'
import { CopilotCommand } from '@pharmsnap/shared/src/types'
import { CHAT_DIALOG_ID } from '@pharmsnap/shared/src/utils/business/copilot'
import { computed, getCurrentInstance, nextTick, ref, watch } from '@vue/composition-api'
import { useLocalStorage } from '@vueuse/core'
import { cloneDeep, isEqual } from 'lodash'
import { setLocalStorage } from 'pharmsnapMF_shared/utils'
import { getTextByUpcaseLang } from 'pharmsnapMF_shared/utils/lang'
import router from '../router'
import { E_ROUTER_NAME } from '../types/enum'

export type CopilotPageContext = {
  page_name: string
  page_url: string
  params?: {
    language?: 'CN' | 'EN'
    // 详情页
    entity_id?: string
    display_name_cn?: string
    display_name_en?: string
    // 列表页
    query?: any
    query_string?: string
    refine?: string
    group_by?: string

    // 列表页选中数据
    data_type?: string
    selected_ids?: string[]
    select_limit?: number
  }
}

const ASSISTANT_DIALOG_VISIBLE_KEY = 'ASSISTANT_DIALOG_VISIBLE'

type QuestionItem = {
  question: string
  questionType?: 'normal' | 'page_summarize'
}

type ExampleConfig = Record<I18nLang, string[]>

type QuestionState = {
  currentQuestions: QuestionItem[]
  generalQuestions: QuestionItem[]
}

type PageSpecialConfig = {
  question: string
  placeholder?: string
}

const getEntityName = (locale: ILang, pageContext?: CopilotPageContext) => {
  return (
    getLangDegraded(
      {
        display_name_cn: pageContext?.params?.display_name_cn,
        display_name_en: pageContext?.params?.display_name_en,
      },
      locale
    ) || ''
  )
}

const PAGE_SPECIAL_CONFIG: Partial<Record<E_ROUTER_NAME, (locale: ILang, pageContext?: CopilotPageContext) => PageSpecialConfig>> = {
  [E_ROUTER_NAME.DRUG]: (locale, pageContext) => {
    const drugName = getEntityName(locale, pageContext)
    return {
      question: getTextByUpcaseLang(
        {
          cn: `总结[${drugName}]这个药物的信息。`,
          en: `Please help me summarize the drug [${drugName}].`,
          jp: `[${drugName}]という医薬品の情報を要約してください。`,
        },
        locale
      ),
      placeholder: getTextByUpcaseLang(
        {
          cn: `询问关于 ${drugName} 的问题`,
          en: `Ask anything about ${drugName}`,
          jp: `${drugName} について質問する`,
        },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.DISEASE]: (locale, pageContext) => {
    const diseaseName = getEntityName(locale, pageContext)
    return {
      question: getTextByUpcaseLang(
        {
          cn: `总结[${diseaseName}]这个疾病的信息。`,
          en: `Please help me summarize the disease [${diseaseName}].`,
          jp: `[${diseaseName}]という疾患の情報を要約してください。`,
        },
        locale
      ),
      placeholder: getTextByUpcaseLang(
        {
          cn: `询问关于 ${diseaseName} 的问题`,
          en: `Ask anything about ${diseaseName}`,
          jp: `${diseaseName} について質問する`,
        },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.ORGANIZATION]: (locale, pageContext) => {
    const orgName = getEntityName(locale, pageContext)
    return {
      question: getTextByUpcaseLang(
        {
          cn: `总结[${orgName}]这个机构的信息。`,
          en: `Please help me summarize the organization [${orgName}].`,
          jp: `[${orgName}]という組織の情報を要約してください。`,
        },
        locale
      ),
      placeholder: getTextByUpcaseLang(
        {
          cn: `询问关于 ${orgName} 的问题`,
          en: `Ask anything about ${orgName}`,
          jp: `${orgName} について質問する`,
        },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.TARGET]: (locale, pageContext) => {
    const targetName = getEntityName(locale, pageContext)
    return {
      question: getTextByUpcaseLang(
        {
          cn: `总结[${targetName}]这个靶点的信息。`,
          en: `Please help me summarize the target [${targetName}].`,
          jp: `[${targetName}]という標的の情報を要約してください。`,
        },
        locale
      ),
      placeholder: getTextByUpcaseLang(
        {
          cn: `询问关于 ${targetName} 的问题`,
          en: `Ask anything about ${targetName}`,
          jp: `${targetName} について質問する`,
        },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.DRUG_TYPE]: (locale, pageContext) => {
    const drugTypeName = getEntityName(locale, pageContext)
    return {
      question: getTextByUpcaseLang(
        {
          cn: `总结[${drugTypeName}]这个药物类型的信息。`,
          en: `Please help me summarize the drug type [${drugTypeName}].`,
          jp: `[${drugTypeName}]というモダリティの情報を要約してください。`,
        },
        locale
      ),
      placeholder: getTextByUpcaseLang(
        {
          cn: `询问关于 ${drugTypeName} 的问题`,
          en: `Ask anything about ${drugTypeName}`,
          jp: `${drugTypeName} について質問する`,
        },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.DRUG_LIST]: (locale) => {
    return {
      question: getTextByUpcaseLang(
        { cn: '总结当前药物列表。', en: 'Summarize this drug list.', jp: '現在の医薬品リストを要約してください。' },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.DRUG_DEAL_LIST]: (locale) => {
    return {
      question: getTextByUpcaseLang(
        { cn: '总结当前药物交易列表。', en: 'Summarize this deal list.', jp: '現在の提携リストを要約してください。' },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.PATENT_LIST]: (locale) => {
    return {
      question: getTextByUpcaseLang(
        { cn: '总结当前专利列表。', en: 'Summarize this patent list.', jp: '現在の特許リストを要約してください。' },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.TRANSLATIONAL_MEDICINE_LIST]: (locale) => {
    return {
      question: getTextByUpcaseLang(
        {
          cn: '总结当前转化医学列表。',
          en: 'Summarize this translational medicine list.',
          jp: '現在のトランスレーショナルリサーチリストを要約してください。',
        },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.CLINICAL_PROGRESS_LIST]: (locale) => {
    return {
      question: getTextByUpcaseLang(
        { cn: '总结当前临床试验列表。', en: 'Summarize this clinical progress list.', jp: '現在の臨床試験リストを要約してください。' },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.CLINICAL_RESULT_LIST]: (locale) => {
    return {
      question: getTextByUpcaseLang(
        { cn: '总结当前临床结果列表。', en: 'Summarize this clinical result list.', jp: '現在の試験結果リストを要約してください。' },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.LITERATURE_LIST]: (locale) => {
    return {
      question: getTextByUpcaseLang(
        { cn: '总结当前文献列表。', en: 'Summarize this literature list.', jp: '現在の文献リストを要約してください。' },
        locale
      ),
    }
  },
  [E_ROUTER_NAME.LATEST_NEWS_LIST]: (locale) => {
    return {
      question: getTextByUpcaseLang(
        { cn: '总结当前资讯列表。', en: 'Summarize this news list.', jp: '現在のニュースリストを要約してください。' },
        locale
      ),
    }
  },
}

function getPageSpecialConfig(pageType: E_ROUTER_NAME, locale: ILang, currentPageContext?: CopilotPageContext) {
  return currentPageContext?.params && PAGE_SPECIAL_CONFIG[pageType]?.(locale, currentPageContext)
}

async function getExampleQuestions(locale: ILang): Promise<QuestionItem[]> {
  const res = await sharedCtx.service.superAgentApi.getConfig<ExampleConfig>('SUPER_AGENT_LS_INPUT_EXAMPLES')
  if (!res.success || !res.data) return []
  const questions = locale === 'CN' ? res.data.cn ?? [] : res.data.en ?? []
  return questions.map((question) => ({
    question,
  }))
}

async function getContextRecommendQuestions(currentPageContext: CopilotPageContext, signal: AbortSignal): Promise<QuestionItem[]> {
  const res = await sharedCtx.service.piaApi.getCopilotRecommendQuestions({
    context: currentPageContext,
    signal,
  })

  if (!res.success || !Array.isArray(res.data.questions)) {
    return []
  }

  return res.data.questions.filter(Boolean).map((question) => ({
    question,
  }))
}

function getPageStaticQuestions(params: {
  pageType: E_ROUTER_NAME
  locale: ILang
  currentPageContext?: CopilotPageContext
  generalQuestions: QuestionItem[]
}): QuestionItem[] {
  const { pageType, locale, currentPageContext, generalQuestions } = params
  const specialConfig = getPageSpecialConfig(pageType, locale, currentPageContext)

  if (!specialConfig) {
    return generalQuestions
  }

  const fallbackQuestions = [...generalQuestions]
  const lastQuestionIndex = fallbackQuestions.length - 1

  if (lastQuestionIndex >= 0) {
    fallbackQuestions[lastQuestionIndex] = {
      question: specialConfig.question,
      questionType: 'page_summarize',
    }
  }

  return fallbackQuestions
}

function getContextRecommendQuestionsTrigger(currentPageContext?: CopilotPageContext) {
  const entityId = currentPageContext?.params?.entity_id

  if (entityId) {
    return {
      page_name: currentPageContext?.page_name,
      entity_id: entityId,
    }
  }

  const query = currentPageContext?.params?.query

  if (query !== undefined) {
    return {
      page_name: currentPageContext?.page_name,
      query,
    }
  }
}

export type CopilotIframeApi = {
  setInputText: (text: string) => Promise<void> | void
  setPlaceholderText: (text: string) => Promise<void> | void
  setPageContext: (pageContext: CopilotPageContext | undefined) => Promise<void> | void
  setRecommendQuestions: (questions: QuestionItem[]) => Promise<void> | void
}

const buildSelectionInputText = (command: CopilotCommand, selectedContent: string, locale: ILang) => {
  if (command === CopilotCommand.start_chat) {
    return selectedContent
  }

  const commandTextMap: Partial<Record<CopilotCommand, { cn: string; en: string; jp: string }>> = {
    [CopilotCommand.summarize]: {
      cn: '总结该段文本',
      en: 'Summarize this text',
      jp: 'このテキストを要約する',
    },
    [CopilotCommand.translate]: {
      cn: '翻译该段文本',
      en: 'Translate this text',
      jp: 'このテキストを翻訳する',
    },
    [CopilotCommand.explain]: {
      cn: '解释该段文本',
      en: 'Explain this text',
      jp: 'このテキストを説明する',
    },
  }
  const commandText = commandTextMap[command]

  if (!commandText) {
    return selectedContent
  }

  // 中文和日文都使用全角引号
  if (locale === 'CN') {
    return `${commandText.cn}“${selectedContent}”`
  }
  if (locale === 'JP') {
    return `${commandText.jp}「${selectedContent}」`
  }
  return `${commandText.en}: "${selectedContent}"`
}

const useCopilotChatStoreFactory = () => {
  const { localeUpcase, dataLang } = useLocale()
  const ins = getCurrentInstance()
  const route = computed(() => ins?.root.proxy.$route)
  const dialogVisible = useLocalStorage<boolean>(ASSISTANT_DIALOG_VISIBLE_KEY, false, {
    writeDefaults: true,
  })
  const pageContext = ref<CopilotPageContext>()
  const isPrinting = ref(false)
  const copilotDialogRightDistanceToViewport = ref(0)
  const copilotIframeApi = ref<CopilotIframeApi | null>(null)
  const pendingInputText = ref('')
  const placeholderText = ref('')
  const questionState = ref<QuestionState>({
    currentQuestions: [],
    generalQuestions: [],
  })
  let contextRecommendQuestionsAbortController: AbortController | undefined
  const contextRecommendQuestionsTrigger = computed(() => getContextRecommendQuestionsTrigger(pageContext.value))

  const updateDistance = () => {
    const copilotElement = document.getElementById(CHAT_DIALOG_ID)
    copilotDialogRightDistanceToViewport.value = copilotElement?.getBoundingClientRect()?.width || 0
  }

  async function syncRecommendQuestionsToIframe() {
    if (!copilotIframeApi.value) return

    try {
      await copilotIframeApi.value.setRecommendQuestions(questionState.value.currentQuestions)
    } catch {
      // iframe 初始化阶段可能短暂不可调用，后续状态更新会继续补发。
    }
  }

  function setCurrentQuestions(questions: QuestionItem[]) {
    questionState.value.currentQuestions = questions
    syncRecommendQuestionsToIframe()
  }

  function abortContextRecommendQuestions() {
    contextRecommendQuestionsAbortController?.abort()
    contextRecommendQuestionsAbortController = undefined
  }

  async function requestContextRecommendQuestions(params: {
    currentPageContext: CopilotPageContext
    currentTrigger: NonNullable<ReturnType<typeof getContextRecommendQuestionsTrigger>>
  }): Promise<QuestionItem[] | undefined> {
    const { currentPageContext, currentTrigger } = params
    abortContextRecommendQuestions()

    const abortController = new AbortController()
    contextRecommendQuestionsAbortController = abortController

    try {
      const questions = await getContextRecommendQuestions(currentPageContext, abortController.signal)

      // 请求返回时 pageContext 可能已经变化，避免旧请求覆盖当前页面的问题。
      if (abortController.signal.aborted || !isEqual(currentTrigger, contextRecommendQuestionsTrigger.value)) {
        return undefined
      }

      return questions
    } catch {
      if (abortController.signal.aborted || !isEqual(currentTrigger, contextRecommendQuestionsTrigger.value)) {
        return undefined
      }
      // 推荐问题接口失败时回退到当前本地推荐逻辑。
      return []
    } finally {
      if (contextRecommendQuestionsAbortController === abortController) {
        contextRecommendQuestionsAbortController = undefined
      }
    }
  }

  async function syncPlaceholderTextToIframe(text: string) {
    placeholderText.value = text
    await nextTick()
    if (!copilotIframeApi.value) return

    try {
      await copilotIframeApi.value.setPlaceholderText(text)
    } catch {
      // iframe 初始化阶段可能短暂不可调用，后续 placeholder 更新会继续补发。
    }
  }

  const questionActions = {
    async initGeneralQuestions() {
      const questions = await getExampleQuestions(localeUpcase.value)
      questionState.value.generalQuestions = questions
      return questions
    },
    async updateCurrentPageQuestions(pageType: E_ROUTER_NAME, currentPageContext?: CopilotPageContext) {
      const currentTrigger = getContextRecommendQuestionsTrigger(currentPageContext)
      const staticQuestions = getPageStaticQuestions({
        pageType,
        locale: localeUpcase.value,
        currentPageContext,
        generalQuestions: questionState.value.generalQuestions,
      })

      if (!currentPageContext || !currentTrigger) {
        abortContextRecommendQuestions()
        setCurrentQuestions(staticQuestions)
        return
      }

      const questions = await requestContextRecommendQuestions({
        currentPageContext,
        currentTrigger,
      })

      if (questions?.length) {
        setCurrentQuestions(questions)
        return
      }

      setCurrentQuestions(staticQuestions)
    },
    updateCurrentPagePlaceholder(pageType: E_ROUTER_NAME, currentPageContext?: CopilotPageContext) {
      const specialConfig = getPageSpecialConfig(pageType, localeUpcase.value, currentPageContext)
      syncPlaceholderTextToIframe(specialConfig?.placeholder || '')
    },
    resetToGeneralQuestions() {
      setCurrentQuestions(questionState.value.generalQuestions)
    },
    getCurrentQuestions() {
      return questionState.value.currentQuestions
    },
  }

  async function syncInputTextToIframe(text: string) {
    pendingInputText.value = text
    await nextTick()
    if (!copilotIframeApi.value) return

    try {
      await copilotIframeApi.value.setInputText(text)
      pendingInputText.value = ''
    } catch {
      // iframe 初始化阶段可能短暂不可调用，保留 pendingInputText 等待下次 ready 后补发。
    }
  }

  async function syncPageContextToIframe() {
    if (!copilotIframeApi.value) return

    try {
      await copilotIframeApi.value.setPageContext(pageContext.value)
    } catch {
      // iframe 初始化阶段可能短暂不可调用，后续 pageContext 更新会继续补发。
    }
  }

  const actions = {
    setCopilotIframeApi: async (api: CopilotIframeApi | null) => {
      copilotIframeApi.value = api
      if (api) {
        await syncPageContextToIframe()
        await syncRecommendQuestionsToIframe()
        await syncPlaceholderTextToIframe(placeholderText.value)
        if (pendingInputText.value) {
          await syncInputTextToIframe(pendingInputText.value)
        }
      }
    },
    showAssistantDialog: (visible: boolean) => {
      dialogVisible.value = visible
      setLocalStorage(ASSISTANT_DIALOG_VISIBLE_KEY, visible.toString())
      nextTick(() => updateDistance())
    },
    scrollToBottom: () => {
      // iframe 内部滚动由子应用自行处理
    },
    setPageContextParams(pageContextParams: CopilotPageContext['params'], ctx?: any) {
      const { page_url = window.location.href || '', page_name = router.currentRoute.name || '' } = ctx || {}
      const currentPageContext = {
        page_name,
        page_url,
        params: {
          ...(pageContextParams as any),
          language: dataLang.value,
        },
      }
      pageContext.value = currentPageContext

      questionActions.updateCurrentPageQuestions(page_name as E_ROUTER_NAME, currentPageContext)
      if (page_name && PAGE_SPECIAL_CONFIG[page_name as E_ROUTER_NAME]) {
        questionActions.updateCurrentPagePlaceholder(page_name as E_ROUTER_NAME, currentPageContext)
      }
      syncPageContextToIframe()
    },
    updatePageContextParams(params: CopilotPageContext['params']) {
      if (!pageContext.value) {
        return
      }
      const prevTrigger = cloneDeep(contextRecommendQuestionsTrigger.value)
      pageContext.value = {
        ...pageContext.value,
        params: {
          ...(pageContext.value?.params || {}),
          ...params,
        },
      }

      const currentPageContext = pageContext.value
      const pageType = currentPageContext.page_name as E_ROUTER_NAME
      const currentTrigger = getContextRecommendQuestionsTrigger(currentPageContext)

      // 只在 entity_id/query 真的变化时刷新问题, 避免列表选中 selected_ids 这类上下文补充导致重复请求
      if (!isEqual(prevTrigger, currentTrigger)) {
        questionActions.updateCurrentPageQuestions(pageType, currentPageContext)
      }

      syncPageContextToIframe()
    },
    async clearSelectedRecordsContext() {
      if (!pageContext.value?.params) {
        return
      }

      const nextParams = {
        ...pageContext.value.params,
      }
      delete nextParams.selected_ids
      delete nextParams.select_limit

      pageContext.value = {
        ...pageContext.value,
        params: nextParams,
      }

      await syncPageContextToIframe()
    },
    getPageContext() {
      return pageContext.value
    },
    clearPageContext() {
      pageContext.value = {
        page_url: window.location.href,
        page_name: route.value?.name || '',
      }
      syncPageContextToIframe()
    },
    updatePlaceholderText(text: string) {
      syncPlaceholderTextToIframe(text)
    },
    handleSelectEmitCommand: async (command: CopilotCommand, selectedContent: string) => {
      actions.showAssistantDialog(true)
      await syncInputTextToIframe(buildSelectionInputText(command, selectedContent, localeUpcase.value))
    },
  }

  watch(
    () => route.value?.name,
    (newVal, oldVal) => {
      if (newVal !== oldVal) {
        actions.clearPageContext()

        if (newVal) {
          questionActions.updateCurrentPageQuestions(newVal as E_ROUTER_NAME, pageContext.value)
          questionActions.updateCurrentPagePlaceholder(newVal as E_ROUTER_NAME, pageContext.value)
        }
      }
    },
    {
      immediate: true,
    }
  )

  return {
    state: {
      dialogVisible,
      isPrinting,
      pageContext,
      placeholderText,
      copilotDialogRightDistanceToViewport,
    },
    actions: {
      ...actions,
      questionActions,
    },
  }
}

export const useCopilotChatStore = getSingle(useCopilotChatStoreFactory)
